/**
 * Preserves the page a user was trying to reach across an external login
 * round-trip (e.g. the Azure B2C / Entra redirect flow).
 *
 * When an unauthenticated user is sent off to the identity provider, the
 * originally requested route is lost: the provider redirects back to the
 * configured `redirectUri`, not to where the user started. We therefore store
 * the intended route just before the redirect and consume it once the user
 * returns authenticated.
 *
 * Backed by localStorage because the MSAL redirect flow performs a full page
 * navigation, so in-memory state does not survive.
 */

const POST_LOGIN_REDIRECT_KEY = 'infuse.postLoginRedirect';

/**
 * How long a stored target stays valid. Guards against sending the user to a
 * long-abandoned destination the next time they happen to authenticate.
 */
const MAX_AGE_MS = 15 * 60 * 1000;

interface StoredRedirect {
  path: string;
  ts: number;
}

function getStorage(): Storage | null {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage;
    }
  } catch {
    // Accessing localStorage can throw (e.g. disabled cookies / privacy mode).
  }
  return null;
}

/**
 * Returns true for paths that are not worth returning to: empty, the root
 * (which just redirects to the default page anyway) or the auth pages
 * themselves (which would trap the user in the login flow).
 */
function isMeaningfulTarget(path: string | undefined | null): path is string {
  if (!path) {
    return false;
  }
  if (path === '/') {
    return false;
  }
  return !path.includes('/auth');
}

/**
 * Remember the page the user was trying to reach before being redirected to
 * log in. No-op for paths that are not worth returning to.
 */
export function savePostLoginRedirect(path: string | undefined | null): void {
  const storage = getStorage();
  if (!storage || !isMeaningfulTarget(path)) {
    return;
  }
  try {
    const payload: StoredRedirect = { path, ts: Date.now() };
    storage.setItem(POST_LOGIN_REDIRECT_KEY, JSON.stringify(payload));
  } catch {
    // Ignore storage errors (quota, privacy mode, ...).
  }
}

/**
 * Read and clear the stored post-login redirect target. Returns null when
 * there is nothing stored or the stored value has expired.
 */
export function consumePostLoginRedirect(): string | null {
  const storage = getStorage();
  if (!storage) {
    return null;
  }

  let raw: string | null = null;
  try {
    raw = storage.getItem(POST_LOGIN_REDIRECT_KEY);
    storage.removeItem(POST_LOGIN_REDIRECT_KEY);
  } catch {
    return null;
  }

  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as StoredRedirect;
    if (!parsed?.path || typeof parsed.ts !== 'number') {
      return null;
    }
    if (Date.now() - parsed.ts > MAX_AGE_MS) {
      return null;
    }
    return isMeaningfulTarget(parsed.path) ? parsed.path : null;
  } catch {
    return null;
  }
}
